/**
 * @fileoverview A collection at its own address: the ordered list of pages that
 * together are a site.
 *
 * THIS IS A SECOND PATH TO THE SAME "NOT AVAILABLE" SCREEN, and the dispatcher
 * that sends readers here warns against exactly that. The warning is about a
 * FASTER path: if a collection that does not exist resolved quicker than a page
 * that does not exist, the difference in timing would say which kind of thing
 * the address named, and for a private page that is the leak the whole design
 * refuses.
 *
 * It is safe here for two reasons, and both have to keep holding:
 *
 * 1. `loadCollection` holds every failure to the same floor `loadPage` uses, so
 *    neither path returns early. The two constants are deliberately separate,
 *    so that tuning one cannot silently move the other, and deliberately equal.
 * 2. The kind is already in the reader's hand. An address names its own kind, so
 *    anyone holding it can decode that locally without asking us anything.
 *    Dispatching on it tells them nothing they did not arrive with.
 *
 * A reader who cannot open the private half is NOT told how much is missing.
 * The count, the gap and the placeholder are all absent on purpose: the public
 * members in the author's order are presented as the whole site, because to that
 * reader they are.
 */

import { useNostrAuth } from '@cloistr/auth';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { PageUnavailable } from '@/components/PageUnavailable';
import { pagePath } from '@/services/nostr/pageLinks';
import { loadCollection, type CollectionLoad } from '@/services/pages/loadCollection';

export function CollectionRoute({ naddr }: { naddr: string }) {
  const { authState, signer } = useNostrAuth();
  const [load, setLoad] = useState<CollectionLoad | null>(null);

  /**
   * KEYED ON THE IDENTITY, NEVER ON THE SIGNER OBJECT. See the long note in
   * `PageRoute`, which carries the mechanism and the measurement: once a session
   * is restored, `@cloistr/auth` is in a self-sustaining effect loop that mints
   * a new signer on every turn of it, so an effect depending on the object
   * re-runs forever -- and this one opens a relay subscription each time. A
   * pubkey is a string and survives the churn; the signer is read through a ref
   * when it is actually used.
   */
  const signerRef = useRef(signer);
  signerRef.current = signer;
  const activeKey = authState.pubkey;

  useEffect(() => {
    let live = true;
    setLoad(null);
    // The signer is passed when there is one, and its ONLY effect is that an
    // owner can open their own private half. A reader without one gets the
    // public site, which is the ordinary case and the one the product exists
    // for: no account, no key, just a link.
    const active = signerRef.current;
    void loadCollection(naddr, active ? { signer: active } : {}).then((result) => {
      if (live) setLoad(result);
    });
    return () => {
      live = false;
    };
  }, [naddr, activeKey]);

  if (!load) {
    return (
      <div className="collection-loading" role="status" aria-live="polite">
        Loading…
      </div>
    );
  }

  if (load.status === 'unavailable') return <PageUnavailable />;

  const { collection } = load;

  return (
    <article className="collection">
      <header className="collection-header">
        <h1>{collection.title}</h1>
        {collection.summary ? <p className="collection-summary">{collection.summary}</p> : null}
      </header>

      {collection.members.length === 0 ? (
        // An empty collection is a real collection. Its author has made a site
        // and not put anything in it yet, which is a different thing from a
        // site that could not be read, and it must not borrow that screen.
        <p className="collection-empty">This collection has no pages yet.</p>
      ) : (
        <ol className="collection-members">
          {collection.members.map((member) => {
            // Linked by ADDRESS, never through a name we resolve, so the link
            // keeps working if our name service does not. The member's kind is
            // whatever its own coordinate carries, which is what lets a
            // collection hold somebody else's long-form article beside our own
            // pages.
            const path = memberPath(member.address);
            return (
              <li key={member.address} className="collection-member">
                {path ? (
                  // The carried title is a DISPLAY LABEL and the member page
                  // stays authoritative. It goes stale when a page is renamed
                  // and refreshes on the collection's next write. The address is
                  // the fallback rather than the other way round, because a
                  // member with no label must still be reachable.
                  <Link to={path}>{member.title ?? member.address}</Link>
                ) : (
                  // A row that will not parse is shown and not linked. A member
                  // list is author-supplied, and a link to nowhere is worse than
                  // visibly inert text.
                  <span className="collection-member-unresolvable">{member.address}</span>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </article>
  );
}

/**
 * A member coordinate as a local route.
 *
 * The coordinate stored in the event is `kind:pubkey:d`, which is NOT what our
 * routes take: they take a NIP-19 identifier, because that is what a reader can
 * paste and what other clients understand. So it is re-encoded here rather than
 * put in the URL raw.
 *
 * Deliberately NOT an `https://pages.cloistr.xyz/...` link. Collection
 * membership is portable content, and hardcoding our hostname into it is the
 * same bug the page renderer already refuses.
 *
 * A coordinate that will not parse yields no link at all rather than a broken
 * one: a member row is author-supplied and may name anything.
 */
function memberPath(address: string): string {
  const [kind, pubkey, identifier] = address.split(':');
  const kindNumber = Number(kind);
  if (!Number.isInteger(kindNumber) || !pubkey || identifier === undefined) return '';
  return pagePath({ kind: kindNumber, pubkey, identifier });
}
